import { type ImageGalleryColorDialogFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { ImageGalleryColorDialogFragmentFragmentDoc } from '@packages/gql/generated/shopping/ImageGalleryColorDialogFragmentFragmentDoc';
import { Box, CloseIconButton, Modal, Slide, Stack, Typography } from '@packages/shared';
import { useTracking } from '@packages/tracking';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ColorPicker32 } from '@packages/themes/icons';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { HorizontalScroll } from '../../HorizontalScroll';
import { ImageGalleryCallToActionButton } from '../../ImageGalleryCallToActionButton';
import { ImageGalleryColorDialogItem } from '../ImageGalleryColorDialogItem';
import { imageGalleryColorDialogMessages } from './messages';

/* GraphQL */ `
  fragment ImageGalleryColorDialogFragment on Product {
    dimensions {
      axisType
      values {
        sku
        variationMainImage
        isSelected
        rgb
        displayText
      }
    }
  }
`;

export interface ImageGalleryColorDialogProps {
  /**
   * Target (COLOR) dimension of the product
   */
  data: FragmentType<typeof ImageGalleryColorDialogFragmentFragmentDoc>;
  /**
   *
   * color change handler
   * @param sku - sku for selected color
   */
  onChange?: (sku: string) => void;
}

export const ImageGalleryColorDialog = ({ data, onChange }: ImageGalleryColorDialogProps) => {
  const { dimensions } = getFragmentData(ImageGalleryColorDialogFragmentFragmentDoc, data);
  const [openColorDialog, setOpenColorDialog] = useState(false);
  const { formatMessage } = useIntl();
  const dispatch = useTracking();

  useEffect(() => {
    if (openColorDialog) {
      window.scroll({ behavior: 'smooth', left: 0, top: 0 });
    }
  }, [openColorDialog]);

  const handleColorPickerClick = () => {
    dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'open',
    });
    setOpenColorDialog(true);
  };

  const dimension = dimensions.find(({ axisType }) => axisType === 'COLOR');

  if (!dimension) {
    return null;
  }
  const modalCloseHandler = () => {
    dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
    setOpenColorDialog(false);
  };

  const onSelect = (
    sku: ImageGalleryColorDialogFragmentFragment['dimensions'][number]['values'][number]['sku'],
  ) => {
    setTimeout(() => setOpenColorDialog(false), 400);

    if (sku) {
      if (typeof onChange === 'function') {
        onChange(sku);
      }

      return setTimeout(() => {
        dispatch({
          event: 'ga_event',
          eventAction: 'rainbowpicker',
          eventCategory: 'userInteraction',
          eventLabel: 'rainbowpicker',
          eventValue: 'variationChange',
        });
      }, 550);
    }

    return dispatch({
      event: 'ga_event',
      eventAction: 'rainbowpicker',
      eventCategory: 'userInteraction',
      eventLabel: 'rainbowpicker',
      eventValue: 'close',
    });
  };

  return (
    <>
      <ImageGalleryCallToActionButton onClick={handleColorPickerClick}>
        <ColorPicker32 />
      </ImageGalleryCallToActionButton>
      {openColorDialog && (
        <Modal open={openColorDialog} onClose={modalCloseHandler}>
          <Slide in={openColorDialog} direction="up">
            <Box
              sx={{
                width: '100%',
                backgroundColor: 'text.light',
                position: 'fixed',
                bottom: 0,
                px: 2,
              }}
            >
              <Stack
                direction="row"
                columnGap={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  my: 2.5,
                }}
              >
                <Typography variant="body2">
                  {formatMessage(imageGalleryColorDialogMessages.selectColor)}
                </Typography>
                <CloseIconButton onClick={modalCloseHandler} />
              </Stack>
              <Box sx={{ mb: 3 }}>
                <HorizontalScroll
                  gap={1.5}
                  scrollToItemIndex={dimension.values.findIndex(({ isSelected }) => isSelected)}
                >
                  {dimension.values.map((value) => (
                    <ImageGalleryColorDialogItem
                      key={value.variationMainImage ?? value.sku}
                      value={value}
                      onSelect={() => onSelect(value.sku)}
                    />
                  ))}
                </HorizontalScroll>
              </Box>
            </Box>
          </Slide>
        </Modal>
      )}
    </>
  );
};
